// The question the boat is asking, and the clock on it.
//
// The anchor assistant asks two questions (ADR-146): "you look anchored — shall
// I set the watch?" on arrival, and "you look like you are leaving — shall I
// clear it?" on departure. Both carry a deadline, and both do something
// definite when it passes: the first arms anyway (fail closed), the second
// leaves the watch set. So the countdown is not decoration — it is the owner's
// only warning that the boat is about to act on their silence.
//
// TWO CLOCKS. `raisedAt` and `deadlineAt` are stamped by the BOAT. The
// countdown runs on the BROWSER. A boat whose clock is off by a minute — which
// happens: no RTC on a Pi, NTP not yet synced after a cold boot — would show a
// countdown a minute wrong, and at the wrong end that means the panel says
// "4:00 left" while the boat arms. So the skew between the two clocks is
// measured from the sample that carried the prompt (`receivedAt` minus the
// sample's own `time`) and taken back out of every countdown. Both numbers are
// already on every `LiveSample`; nothing new has to be published.
//
// STALENESS. Nothing expires telemetry, so a prompt that the boat settled while
// this tab was asleep is still sitting in the sample map. Past its deadline
// plus a minute of grace, the prompt has been decided one way or another and
// must stop looking live — a countdown reading "-14:22" or a frozen "0:00" is
// worse than nothing.
//
// NON-OPTIMISTIC. Answering posts to the cloud, which relays to the boat, which
// settles the prompt and republishes `computed.anchor.prompt` as null. THAT
// null is the confirmation — not the 202, which only says the cloud took the
// frame. So the countdown keeps running after an answer, and if the telemetry
// has not flipped a few seconds later the panel says so plainly rather than
// pretending the answer landed.
//
// No React, no DOM.

/** How long past its deadline a prompt is still worth showing. */
export const PROMPT_GRACE_MS = 60_000;

/** How long an answer may sit unconfirmed before the panel says it may be lost. */
export const PROMPT_RESPONSE_CONFIRM_MS = 5_000;

export const PROMPT_UNCONFIRMED_COPY = 'Your answer may not have reached the boat.';

export type AnchorPromptKind = 'first_arm' | 'departure';

export interface AnchorPromptView {
  readonly kind: AnchorPromptKind;
  readonly promptId: string;
  readonly raisedAt: string;
  readonly deadlineAt: string;
}

/** The answers the boat accepts, per prompt kind. */
export type AnchorPromptResponse = 'arm' | 'decline' | 'confirm_departure' | 'not_departing';

/**
 * Read `computed.anchor.prompt`. Every field is required: a prompt without a
 * deadline cannot be counted down and a prompt without an id cannot be
 * answered, so a partial one reads as no question at all rather than as a
 * banner with dead buttons.
 */
export function readAnchorPrompt(raw: unknown): AnchorPromptView | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (value.kind !== 'first_arm' && value.kind !== 'departure') return null;
  const promptId = value.promptId;
  const raisedAt = value.raisedAt;
  const deadlineAt = value.deadlineAt;
  if (typeof promptId !== 'string' || promptId.length === 0) return null;
  if (typeof raisedAt !== 'string' || typeof deadlineAt !== 'string') return null;
  if (!Number.isFinite(Date.parse(deadlineAt))) return null;
  return { kind: value.kind, promptId, raisedAt, deadlineAt };
}

/**
 * How far this browser's clock is ahead of the boat's, from the sample that
 * carried the prompt. `0` when the sample's own time is unreadable — no
 * correction is better than a correction built on a bad parse.
 */
export function clockSkewMs(sample: { time?: string; receivedAt?: number } | null): number {
  if (sample === null || sample === undefined) return 0;
  const sampleMs = typeof sample.time === 'string' ? Date.parse(sample.time) : NaN;
  const received = sample.receivedAt;
  if (!Number.isFinite(sampleMs) || typeof received !== 'number' || !Number.isFinite(received)) {
    return 0;
  }
  return received - sampleMs;
}

export interface PromptCountdown {
  /** Milliseconds left on the BOAT's clock; never negative. */
  readonly remainingMs: number;
  /** "4:31" — minutes and seconds, the shape a countdown is read in. */
  readonly text: string;
  /** The deadline has passed but the boat has not republished yet. */
  readonly expired: boolean;
  /**
   * Past the deadline AND past the grace: the boat has settled this and we are
   * looking at a leftover. The banner must not render.
   */
  readonly stale: boolean;
}

export function promptCountdown(input: {
  prompt: AnchorPromptView;
  skewMs: number;
  nowMs: number;
}): PromptCountdown {
  const deadlineMs = Date.parse(input.prompt.deadlineAt);
  // The browser's now, expressed on the boat's clock.
  const boatNowMs = input.nowMs - input.skewMs;
  const remainingRaw = Number.isFinite(deadlineMs) ? deadlineMs - boatNowMs : 0;
  const remainingMs = Math.max(0, remainingRaw);
  return {
    remainingMs,
    text: formatCountdown(remainingMs),
    expired: remainingRaw <= 0,
    stale: remainingRaw <= -PROMPT_GRACE_MS,
  };
}

/** "4:31", "0:07" — always m:ss, because a bare seconds count reads as an error. */
export function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// What the banner says
// ---------------------------------------------------------------------------

export interface PromptCopy {
  readonly question: string;
  /** What happens if nobody answers — the whole point of showing a clock. */
  readonly ifIgnored: string;
  readonly actions: ReadonlyArray<{
    readonly response: AnchorPromptResponse;
    readonly label: string;
    /** The one that continues what the boat already believes. */
    readonly primary: boolean;
  }>;
}

const FIRST_ARM_COPY: PromptCopy = {
  question: 'It looks like you have anchored. Set the anchor watch?',
  ifIgnored: 'If nobody answers, the boat sets it anyway.',
  actions: [
    { response: 'arm', label: 'Set the anchor watch', primary: true },
    { response: 'decline', label: 'Not anchored', primary: false },
  ],
};

const DEPARTURE_COPY: PromptCopy = {
  question: 'It looks like you are leaving. Clear the anchor watch?',
  ifIgnored: 'If nobody answers, the anchor watch stays set.',
  actions: [
    { response: 'confirm_departure', label: 'Yes, we are leaving', primary: true },
    { response: 'not_departing', label: 'Still anchored', primary: false },
  ],
};

export function promptCopy(kind: AnchorPromptKind): PromptCopy {
  return kind === 'first_arm' ? FIRST_ARM_COPY : DEPARTURE_COPY;
}

/** Offered under the first-arm answer: stop asking next time. */
export const REMEMBER_LABEL = 'Do this automatically from now on';

// ---------------------------------------------------------------------------
// Answering, without pretending
// ---------------------------------------------------------------------------

export interface PromptResponseState {
  /** The prompt we answered, so a NEW prompt does not inherit the wait. */
  readonly promptId: string;
  readonly response: AnchorPromptResponse;
  /** When the 202 came back — the wait starts at the cloud's ack, not the click. */
  readonly acceptedAtMs: number | null;
  /** The send itself failed; this message is shown instead of a wait. */
  readonly error: string | null;
}

/**
 * Whether to warn that an answer may not have landed.
 *
 * True only while the prompt is STILL LIVE in telemetry: the flip to null is
 * the confirmation, so a prompt that has gone is an answer that arrived, no
 * matter how slow the round trip felt.
 */
export function promptAnswerUnconfirmed(input: {
  sent: PromptResponseState | null;
  livePrompt: AnchorPromptView | null;
  nowMs: number;
}): boolean {
  const sent = input.sent;
  if (sent === null || sent.acceptedAtMs === null || sent.error !== null) return false;
  if (input.livePrompt === null) return false;
  if (input.livePrompt.promptId !== sent.promptId) return false;
  return input.nowMs - sent.acceptedAtMs >= PROMPT_RESPONSE_CONFIRM_MS;
}

/** True while an answer is out and the boat has not confirmed it either way. */
export function promptAnswerPending(input: {
  sent: PromptResponseState | null;
  livePrompt: AnchorPromptView | null;
}): boolean {
  const sent = input.sent;
  if (sent === null || sent.error !== null) return false;
  return input.livePrompt !== null && input.livePrompt.promptId === sent.promptId;
}

/**
 * Whether a recorded answer still belongs to the question on screen. A new
 * prompt id means a new question, and the previous answer's state — including
 * its "may not have reached the boat" warning — must not carry over.
 */
export function responseStillApplies(
  sent: PromptResponseState | null,
  prompt: AnchorPromptView | null
): boolean {
  return sent !== null && prompt !== null && sent.promptId === prompt.promptId;
}

// ---------------------------------------------------------------------------
// The banner's one status line
// ---------------------------------------------------------------------------

/** The telemetry path the question rides on (core `ANCHOR_PROMPT_PATH`). */
export const ANCHOR_PROMPT_PATH = 'computed.anchor.prompt';

/**
 * Shown in place of a live countdown once the deadline has passed but the boat
 * has not republished yet. Inside the grace window the question is still worth
 * showing — the answer may still land, and the boat may simply be a few
 * seconds behind — but the clock on it is no longer a promise.
 */
export const PROMPT_EXPIRED_COPY = 'This countdown may have already ended.';

export const PROMPT_SENDING_COPY = 'Sending your answer…';
export const PROMPT_WAITING_COPY = 'Waiting for the boat to confirm…';

export interface PromptStatusLine {
  readonly text: string;
  /** `pending` is a wait; `problem` needs the owner to look at it. */
  readonly tone: 'pending' | 'problem';
}

/**
 * What to say under the buttons about the answer that was given — nothing at
 * all until one is.
 *
 * The order is the honest one: a send that failed outright is the most useful
 * thing to say, then a send still in flight, then an accepted answer the boat
 * has not acted on. There is deliberately no success line: the boat confirming
 * means the prompt disappears, and so does this whole banner.
 */
export function promptStatusLine(input: {
  sent: PromptResponseState | null;
  livePrompt: AnchorPromptView | null;
  nowMs: number;
}): PromptStatusLine | null {
  const sent = input.sent;
  if (sent === null) return null;
  if (sent.error !== null) return { text: sent.error, tone: 'problem' };
  if (sent.acceptedAtMs === null) return { text: PROMPT_SENDING_COPY, tone: 'pending' };
  if (promptAnswerUnconfirmed(input)) return { text: PROMPT_UNCONFIRMED_COPY, tone: 'problem' };
  if (promptAnswerPending(input)) return { text: PROMPT_WAITING_COPY, tone: 'pending' };
  return null;
}

/**
 * A failed send, in the owner's words. Deliberately not
 * `anchorCommand.describeSendError`: this is an answer to a question, not a
 * command, and "the command was refused" reads as though the boat rejected the
 * anchoring rather than that a message did not get through.
 */
export function describeAnswerError(err: unknown): string {
  const body = (err as { body?: unknown } | null)?.body;
  if (body !== null && typeof body === 'object') {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status === 'number') return `Your answer was refused (HTTP ${status}).`;
  return 'Your answer could not be sent — check your connection.';
}
